import type {JsonSchema4, JsonSchema7} from '@jsonforms/core'

const BACKWARD_COMPATIBLE_PRIMITIVES = ['boolean', 'integer']

const NUMERIC_TYPES = ['integer', 'number'] as const

export type SchemaProperty = JsonSchema4 & JsonSchema7

/** `type` of a schema property: `string | string[] | undefined` in the JSON Forms typings. */
export type SchemaType = SchemaProperty['type']

export type NumericType = (typeof NUMERIC_TYPES)[number]

type SchemaWithDefs = SchemaProperty & {$defs?: Record<string, SchemaProperty>}

export function isNumericType(type: SchemaType): type is NumericType {
  return NUMERIC_TYPES.some(numericType => numericType === type)
}

export function normalizeProperty(property: SchemaProperty): SchemaProperty {
  const type = property?.type
  if (typeof type === 'string' && BACKWARD_COMPATIBLE_PRIMITIVES.includes(type)) {
    return {anyOf: [{type}, {type: 'string'}]}
  }

  if (Array.isArray(property?.oneOf) || Array.isArray(property?.anyOf)) {
    return {anyOf: [property, {type: 'string'}]}
  }

  return property
}

function relaxNumericSchemaMap(
  subSchemas: Record<string, SchemaProperty>,
): Record<string, SchemaProperty> {
  return Object.fromEntries(
    Object.entries(subSchemas).map(([name, subSchema]) => [name, relaxNumericTypes(subSchema)]),
  )
}

export function relaxNumericTypes(schema: SchemaProperty): SchemaProperty {
  if (schema == null || typeof schema !== 'object') {
    return schema
  }

  const source = schema as SchemaWithDefs
  const result: SchemaWithDefs = {...source}

  // an enum is rendered as a select, so it neither needs nor tolerates a relaxed type
  if (isNumericType(schema.type) && schema.enum == null) {
    result.type = [schema.type, 'string']
  }
  if (Array.isArray(schema.required)) {
    result.required = [...new Set(schema.required)]
  }
  if (schema.properties != null) {
    result.properties = relaxNumericSchemaMap(schema.properties as Record<string, SchemaProperty>)
  }
  if (schema.definitions != null) {
    result.definitions = relaxNumericSchemaMap(schema.definitions as Record<string, SchemaProperty>)
  }
  if (source.$defs != null) {
    result.$defs = relaxNumericSchemaMap(source.$defs)
  }
  if (Array.isArray(schema.oneOf)) {
    result.oneOf = schema.oneOf.map(branch => relaxNumericTypes(branch as SchemaProperty))
  }
  if (Array.isArray(schema.anyOf)) {
    result.anyOf = schema.anyOf.map(branch => relaxNumericTypes(branch as SchemaProperty))
  }

  return result
}

const relaxedSchemas = new WeakMap<object, SchemaProperty>()

export function getRelaxedSchema(schema: SchemaProperty): SchemaProperty {
  if (schema == null || typeof schema !== 'object') {
    return schema
  }

  const cached = relaxedSchemas.get(schema)
  if (cached != null) {
    return cached
  }

  const relaxed = relaxNumericTypes(schema)
  relaxedSchemas.set(schema, relaxed)
  return relaxed
}

export function normalizeSchemaProperties(schema: SchemaProperty): SchemaProperty {
  const properties = schema.properties
  if (properties == null) {
    return schema
  }
  return {
    ...schema,
    properties: Object.fromEntries(
      Object.entries(properties).map(([name, prop]) => [name, normalizeProperty(prop)]),
    ),
  } as SchemaProperty
}
